import { List } from "immutable";
import { SimpleMappedStore } from "@core/store/mappedStore";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore } from "@core/store/store";
import { Media } from "@domain/media/media";
import { Workspace } from "./workspace";
import { WorkspaceApi } from "./workspaceApi";
import { WorkspaceDraft } from "./workspaceDraft";

export class WorkspaceService {
	workspaceStore = new SimpleStore((id) => this.workspaceApi.get(id));
	slugWorkspaceStore = new SimpleStore<Workspace>((slugWorkspace) => this.workspaceApi.getBySlug(slugWorkspace));
	shareroomsWorkspacesStore = new SimpleMappedStorePresentation(this.workspaceStore, (id, page) =>
		this.workspaceApi.list(id, page)
	);

	coursesWorkspacesStore = new SimpleMappedStorePresentation(this.workspaceStore, (id, page) =>
		this.workspaceApi.listForCourse(id, page)
	);
	quizzesWorkspacesStore = new SimpleMappedStorePresentation(this.workspaceStore, (id, page) =>
		this.workspaceApi.listForQuiz(id, page)
	);

	articlesWorkspacesStore = new SimpleMappedStorePresentation(this.workspaceStore, (id, page) =>
		this.workspaceApi.listForArticle(id, page)
	);

	assetsStore = new SimpleMappedStore((id, page) => this.workspaceApi.getAssets(id, page));

	constructor(
		private readonly workspaceApi: WorkspaceApi,
		initialWorkspaces?: Map<string, Workspace>
	) {
		if (initialWorkspaces) {
			this.workspaceStore.merge(List(initialWorkspaces.values()));
		}
		this.slugWorkspaceStore.bind(this.workspaceStore);
	}

	async save(draft: WorkspaceDraft, workspaceId?: string): Promise<Workspace> {
		const workspace = workspaceId
			? await this.workspaceApi.save(draft, workspaceId)
			: await this.workspaceApi.create(draft);
		this.workspaceStore.save(workspace);
		return workspace;
	}

	async delete(id: string, shareroomId: string): Promise<void> {
		await this.workspaceApi.delete(id);
		this.workspaceStore.remove(id);
		this.shareroomsWorkspacesStore.fetch(shareroomId);
	}

	async addAsset(workspaceId: string, asset: Media): Promise<void> {
		await this.workspaceApi.addAsset(workspaceId, asset.id);
		this.assetsStore.updateContent(workspaceId, (assets) => assets.unshift(asset));
		this.assetsStore.update(workspaceId, (data) => ({ ...data, total_size: data.total_size + 1 }));
	}

	async deleteAsset(workspaceId: string, id: string): Promise<void> {
		await this.workspaceApi.deleteAsset(workspaceId, id);
		this.assetsStore.updateContent(workspaceId, (assets) => assets.remove(assets.findIndex((a) => a.id === id)));
		this.assetsStore.update(workspaceId, (data) => ({ ...data, total_size: data.total_size - 1 }));
	}

	async addCourseToWorkspaces(courseId: string, workspaces: string[]): Promise<void> {
		for (const workspaceId of workspaces) {
			await this.workspaceApi.addCourse(workspaceId, courseId);
		}
	}

	async removeCourseFromWorkspaces(courseId: string, workspaces: string[]): Promise<void> {
		for (const workspaceId of workspaces) {
			await this.workspaceApi.deleteCourse(workspaceId, courseId);
		}
	}

	async addQuizToWorkspaces(quizId: string, workspaces: string[]): Promise<void> {
		for (const workspaceId of workspaces) {
			await this.workspaceApi.addQuiz(workspaceId, quizId);
		}
	}

	async removeQuizFromWorkspaces(quizId: string, workspaces: string[]): Promise<void> {
		for (const workspaceId of workspaces) {
			await this.workspaceApi.deleteQuiz(workspaceId, quizId);
		}
	}
}
